import flatten from "lodash/flatten";
import { useMemo } from "react";
import { keepPreviousData, useInfiniteQuery } from "react-query";

import type {
  ConnectionRequest,
  GetConnectionRequestsResponse,
} from "@/react/api/connectionRequests/connectionRequestsApi";
import { connectionRequestsApi } from "@/react/api/connectionRequests/connectionRequestsApi";

const DEFAULT_PAGE_SIZE = 10;
export const CONNECTION_REQUESTS_QUERY_KEY = "connection-requests";

interface UseConnectionRequestsInfiniteQueryProps {
  initialPage?: number;
  options?: object;
  perPage?: number;
}

export const useConnectionRequestsInfiniteQuery = ({
  perPage = DEFAULT_PAGE_SIZE,
  initialPage = 1,
  options = {},
}: UseConnectionRequestsInfiniteQueryProps = {}) => {
  const fetchPage = ({ pageParam = initialPage }) => {
    let page = 1;

    if (typeof pageParam === "number" && pageParam > 0) {
      page = pageParam;
    }

    return connectionRequestsApi.getRequests({
      page,
      per_page: perPage,
    });
  };

  const queryKey = [CONNECTION_REQUESTS_QUERY_KEY, perPage, initialPage];

  const queryOptions = {
    getNextPageParam: (lastPage: GetConnectionRequestsResponse) =>
      lastPage.has_next_page ? lastPage.page + 1 : undefined,
    getPreviousPageParam: (firstPage: GetConnectionRequestsResponse) =>
      firstPage.page > 1 ? firstPage.page - 1 : undefined,
    placeholderData: keepPreviousData,
    ...options,
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoading,
    refetch,
  } = useInfiniteQuery({
    initialPageParam: initialPage,
    queryKey,
    queryFn: fetchPage,
    ...queryOptions,
  });
  const pages = useMemo(() => data?.pages || [], [data?.pages]);
  const totalCount = pages[0]?.count || 0;
  const requests: ConnectionRequest[] = useMemo(
    () => flatten(pages.map(page => page.records)),
    [pages],
  );

  return {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isInitialLoading,
    refetch,
    totalCount,
    requests,
  };
};
